/**
 * Sales calls (docs/specs/sales-customers.md §4): the branch page (Today, Month, People,
 * Data health), Targets (monthly per branch with history), Settings (weights, bands, CDisc
 * exclusions) and the Home "My branch today" card. Nothing polls: `sales.updated` frames on
 * the realtime socket invalidate these keys (app/realtime.tsx).
 */
import {
  CustomerSignalRulesSchema,
  SalesBranchCustomersSchema,
  SalesBranchesResponseSchema,
  SalesCallsSchema,
  SalesHealthSchema,
  SalesHomeResponseSchema,
  SalesLeagueSchema,
  SalesPromotionsSchema,
  SalesQuoteDetailSchema,
  SalesQuotesSchema,
  SalesMonthSchema,
  SalesOkEmptySchema,
  SalesOkSchema,
  SalesPeopleSchema,
  SalesSettingsSchema,
  SalesTargetHistorySchema,
  SalesTargetsResponseSchema,
  SalesTodaySchema,
  type CustomerSignalRuleWrite,
  type InitialsDispositionWrite,
  type SalesSettingsPatch,
  type SalesTargetsBulkWrite,
  type SalesTargetWrite,
  type WorkingDayWeightsWrite,
} from '@aroo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './client';
import { peopleKeys } from './people';

const enc = encodeURIComponent;

export const salesKeys = {
  all: ['sales'] as const,
  branches: ['sales', 'branches'] as const,
  today: (siteId: string) => ['sales', 'branch', siteId, 'today'] as const,
  month: (siteId: string, period: string | null) =>
    ['sales', 'branch', siteId, 'month', period] as const,
  people: (siteId: string, period: string | null) =>
    ['sales', 'branch', siteId, 'people', period] as const,
  health: (siteId: string, period: string | null) =>
    ['sales', 'branch', siteId, 'health', period] as const,
  customers: (siteId: string) => ['sales', 'branch', siteId, 'customers'] as const,
  quotes: (siteId: string) => ['sales', 'branch', siteId, 'quotes'] as const,
  quote: (document: string) => ['sales', 'quote', document] as const,
  calls: (siteId: string) => ['sales', 'branch', siteId, 'calls'] as const,
  promotions: (siteId: string) => ['sales', 'branch', siteId, 'promotions'] as const,
  league: (period: string | null, regionId: string | null) =>
    ['sales', 'league', period, regionId] as const,
  signalRules: ['sales', 'settings', 'signal-rules'] as const,
  targets: (period: string | null) => ['sales', 'targets', period] as const,
  history: (siteId: string) => ['sales', 'targets', 'history', siteId] as const,
  settings: ['sales', 'settings'] as const,
  home: ['sales', 'home'] as const,
};

/** Paths for CSV downloads (plain links: the session cookie rides along). */
export const salesCsv = {
  people: (siteId: string, period: string | null) =>
    `/api/v1/sales/branches/${enc(siteId)}/people/csv${period ? `?period=${enc(period)}` : ''}`,
  exceptions: (siteId: string, period: string | null) =>
    `/api/v1/sales/branches/${enc(siteId)}/health/exceptions/csv${period ? `?period=${enc(period)}` : ''}`,
  quotes: (siteId: string) => `/api/v1/sales/branches/${enc(siteId)}/quotes/csv`,
  league: (period: string | null, regionId: string | null) => {
    const p = new URLSearchParams();
    if (period) p.set('period', period);
    if (regionId) p.set('regionId', regionId);
    const s = p.toString();
    return `/api/v1/sales/league/csv${s ? `?${s}` : ''}`;
  },
};

const periodQuery = (period: string | null) => (period ? { period } : undefined);

export function useSalesBranches(enabled = true) {
  return useQuery({
    queryKey: salesKeys.branches,
    queryFn: () => apiGet('/sales/branches', SalesBranchesResponseSchema),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSalesToday(siteId: string) {
  return useQuery({
    queryKey: salesKeys.today(siteId),
    queryFn: () => apiGet(`/sales/branches/${enc(siteId)}/today`, SalesTodaySchema),
    enabled: !!siteId,
  });
}

export function useSalesMonth(siteId: string, period: string | null) {
  return useQuery({
    queryKey: salesKeys.month(siteId, period),
    queryFn: () =>
      apiGet(`/sales/branches/${enc(siteId)}/month`, SalesMonthSchema, periodQuery(period)),
    enabled: !!siteId,
  });
}

export function useSalesPeople(siteId: string, period: string | null) {
  return useQuery({
    queryKey: salesKeys.people(siteId, period),
    queryFn: () =>
      apiGet(`/sales/branches/${enc(siteId)}/people`, SalesPeopleSchema, periodQuery(period)),
    enabled: !!siteId,
  });
}

export function useSalesHealth(siteId: string, period: string | null) {
  return useQuery({
    queryKey: salesKeys.health(siteId, period),
    queryFn: () =>
      apiGet(`/sales/branches/${enc(siteId)}/health`, SalesHealthSchema, periodQuery(period)),
    enabled: !!siteId,
  });
}

export function useSalesTargets(period: string | null, enabled = true) {
  return useQuery({
    queryKey: salesKeys.targets(period),
    queryFn: () => apiGet('/sales/targets', SalesTargetsResponseSchema, periodQuery(period)),
    enabled,
  });
}

export function useSalesTargetHistory(siteId: string | null) {
  return useQuery({
    queryKey: salesKeys.history(siteId ?? ''),
    queryFn: () => apiGet(`/sales/targets/${enc(siteId ?? '')}/history`, SalesTargetHistorySchema),
    enabled: !!siteId,
  });
}

function useInvalidateSales() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: salesKeys.all });
}

export function useSetTarget() {
  const invalidate = useInvalidateSales();
  return useMutation({
    mutationFn: (body: SalesTargetWrite) => apiPost('/sales/targets', SalesOkSchema, body),
    onSuccess: () => invalidate(),
  });
}

export function useSetTargetsBulk() {
  const invalidate = useInvalidateSales();
  return useMutation({
    mutationFn: (body: SalesTargetsBulkWrite) =>
      apiPost('/sales/targets/bulk', SalesOkSchema, body),
    onSuccess: () => invalidate(),
  });
}

export function useSalesSettings(enabled = true) {
  return useQuery({
    queryKey: salesKeys.settings,
    queryFn: () => apiGet('/sales/settings', SalesSettingsSchema),
    enabled,
  });
}

export function usePatchSalesSettings() {
  const invalidate = useInvalidateSales();
  return useMutation({
    mutationFn: (patch: SalesSettingsPatch) =>
      apiPatch('/sales/settings', SalesSettingsSchema, patch),
    onSuccess: () => invalidate(),
  });
}

export function useSetWeights() {
  const invalidate = useInvalidateSales();
  return useMutation({
    mutationFn: ({ siteId, weights }: { siteId: string; weights: WorkingDayWeightsWrite }) =>
      apiPut(`/sales/settings/weights/${enc(siteId)}`, SalesSettingsSchema, weights),
    onSuccess: () => invalidate(),
  });
}

export function useSalesHome(enabled = true) {
  return useQuery({
    queryKey: salesKeys.home,
    queryFn: () => apiGet('/sales/home', SalesHomeResponseSchema),
    enabled,
    staleTime: 60_000,
  });
}

// S-M2: Customers lenses, the league, initials dispositions, signal rules ----------------

export function useSalesBranchCustomers(siteId: string) {
  return useQuery({
    queryKey: salesKeys.customers(siteId),
    queryFn: () => apiGet(`/sales/branches/${enc(siteId)}/customers`, SalesBranchCustomersSchema),
    enabled: !!siteId,
  });
}

export function useSalesLeague(period: string | null, regionId: string | null, enabled = true) {
  return useQuery({
    queryKey: salesKeys.league(period, regionId),
    queryFn: () =>
      apiGet('/sales/league', SalesLeagueSchema, {
        ...(period ? { period } : {}),
        ...(regionId ? { regionId } : {}),
      }),
    enabled,
    placeholderData: (prev) => prev,
  });
}

const Ok = SalesOkEmptySchema;

export function useSetInitialsDisposition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      siteId,
      initials,
      ...body
    }: { siteId: string; initials: string } & InitialsDispositionWrite) =>
      // prettier-ignore
      apiPut(`/sales/initials/${enc(siteId)}/${enc(initials)}`, Ok, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
      void qc.invalidateQueries({ queryKey: peopleKeys.queues });
    },
  });
}

export function useClearInitialsDisposition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, initials }: { siteId: string; initials: string }) =>
      apiDelete(`/sales/initials/${enc(siteId)}/${enc(initials)}`, Ok),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
      void qc.invalidateQueries({ queryKey: peopleKeys.queues });
    },
  });
}

export function useSignalRules(enabled = true) {
  return useQuery({
    queryKey: salesKeys.signalRules,
    queryFn: () => apiGet('/sales/settings/signal-rules', CustomerSignalRulesSchema),
    enabled,
  });
}

export function usePatchSignalRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, ...body }: { code: string } & CustomerSignalRuleWrite) =>
      apiPut(`/sales/settings/signal-rules/${enc(code)}`, CustomerSignalRulesSchema, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: salesKeys.signalRules }),
  });
}

// S-M3: quotes, calls, promotions --------------------------------------------------------

/** The branch's Quotes block; the API checks live stock for the open quotes (bounded). */
export function useSalesQuotes(siteId: string) {
  return useQuery({
    queryKey: salesKeys.quotes(siteId),
    queryFn: () => apiGet(`/sales/branches/${enc(siteId)}/quotes`, SalesQuotesSchema),
    enabled: !!siteId,
    staleTime: 60_000,
  });
}

/** The quote drawer: lines, free stock here and at the nearest branches, linked invoices. */
export function useSalesQuote(document: string | null) {
  return useQuery({
    queryKey: salesKeys.quote(document ?? ''),
    queryFn: () => apiGet(`/sales/quotes/${enc(document ?? '')}`, SalesQuoteDetailSchema),
    enabled: !!document,
    staleTime: 60_000,
  });
}

export function useSalesCalls(siteId: string) {
  return useQuery({
    queryKey: salesKeys.calls(siteId),
    queryFn: () => apiGet(`/sales/branches/${enc(siteId)}/calls`, SalesCallsSchema),
    enabled: !!siteId,
    staleTime: 5 * 60_000,
  });
}

export function useSalesPromotions(siteId: string) {
  return useQuery({
    queryKey: salesKeys.promotions(siteId),
    queryFn: () => apiGet(`/sales/branches/${enc(siteId)}/promotions`, SalesPromotionsSchema),
    enabled: !!siteId,
    staleTime: 5 * 60_000,
  });
}
